import { useQuery } from '@tanstack/react-query';
import { mapToPracticeInfo, practiceInfoKey, requestMyPracticeInfo } from './practiceApi';

/**
 * The office of the physician the authenticated member is assigned to. A `NOT_FOUND` error code
 * means the member has no assigned practice on file — a reachable, non-alarming state the caller
 * should render as an empty state rather than an error (see `PracticeInfoEmpty`). Any other code is
 * a genuine failure (see `PracticeInfoError`).
 *
 * `enabled` defaults to `true` for callers that always need the data (ProfilePage,
 * useMembershipCard). A caller mounted regardless of visibility — a modal that stays in the tree
 * across its own open/close, for instance — should pass its own open state through here so the
 * query doesn't fire on every render it happens to be present for.
 */
export function usePracticeInfo(options?: { enabled?: boolean }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: practiceInfoKey,
    queryFn: requestMyPracticeInfo,
    select: mapToPracticeInfo,
    enabled: options?.enabled ?? true,
  });

  // Exposes `error` (not just the isError flag) so the caller can branch on extractGqlErrorCode
  // instead of a generic message.
  return { data, isPending, isError, error };
}
